import { AfterContentChecked, Component, EventEmitter, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { DISPLAY_DATE_FORMAT, EVIDENCE_URL } from '../../../app.constants';
import { Audit, Capa, CapaECTypes, CapaStatus, Evidence, EvidenceStatus, Finding, EvidenceApproval, EvidenceApprovalResult } from '../../audit.model';
import { BreadcrumbItem, RequestResult, TableData, TableOptions } from '../../../shared/shared.model';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { AuditService } from '../../audit.service';
import { FindingService } from '../../finding.service';
import { CapaService } from '../../capa.service';
import { EvidenceService } from '../../evidence.service';
import { MatDialog, MatSnackBar } from '@angular/material';
import { filter, switchMap, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { EvidenceAddDialogComponent } from '../evidence-add-dialog/evidence-add-dialog.component';
import { OkCancelDialogComponent } from '../../../shared/components/ok-cancel-dialog/ok-cancel-dialog.component';
import { Sid } from '../../sid.model';

@Component({
  selector: 'iaud-evidence-approval-list',
  templateUrl: './evidence-approval-list.component.html',
  styleUrls: ['./evidence-approval-list.component.scss']
})
export class EvidenceApprovalListComponent implements OnInit, AfterContentChecked {
  @ViewChild('fileCellTemplate') fileCellTemplate: TemplateRef<any>;
  @ViewChild('statusCellTemplate') statusCellTemplate: TemplateRef<any>;
  @ViewChild('reasonCellTemplate') reasonCellTemplate: TemplateRef<any>;

  evUrl = EVIDENCE_URL;

  Sid : Sid;
  
  auditId: number;
  findingId: number;
  capaId: number;
  sidId: number;

  audit: Audit;
  finding: Finding;
  capa: Capa;

  capaTypes = CapaECTypes;
  capaType: string;
  capaStatus = CapaStatus;
  evidenceStatus = EvidenceStatus;  

  evidenceTableData: TableData<Evidence>;
  forceReloadEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  canSubmit: boolean = false;
  modelDirty: boolean = false;

  hasInitialEvidences: boolean = false;

  canApprove:boolean=false;

  evidenceTableOptions: TableOptions = {
    actionButtons: false,
    hideDeleteButton:false,
    columns: [
      {
        title: 'Status',
        fieldName: 'status',
      },
      {
        title: 'Reason',
        fieldName: 'reason',
      },
      {
        title: 'Evidence ID',
        fieldName: 'evidenceId',
      },
      {
        title: 'Evidence File',
        fieldName: 'fileName',
      },
      {
        title: 'Created By',
        fieldName: 'createdBy',
      },
      {
        title: 'Created Date',
        fieldName: 'createdOn',
        pipe: new DatePipe('en-US'),
        pipeParams: DISPLAY_DATE_FORMAT,
      },
      {
        title: 'Updated By',
        fieldName: 'modifiedBy',
      },
      {
        title: 'Updated Date',
        fieldName: 'modifiedOn',
        pipe: new DatePipe('en-US'),
        pipeParams: DISPLAY_DATE_FORMAT,
      },
    ],
    additionalInfo: [
      {
        title: 'Evidence Description',
        fieldName: 'description',
      },
      {
        title: 'Status',
        fieldName: 'status',
      },
      {
        title: 'Reason',
        fieldName: 'reason',
      },
    ],
  };

  breadcrumbs: Array<BreadcrumbItem> = [
    {
      title: 'Home',
      path: '/home',
    },
    {
      title: 'All audits',
      path: '/audit',
    },
  ];

  hasHistory : boolean = true;

  //https://stackblitz.com/edit/angular-g5u7cy?file=app%2Ftable-editing-example.html

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private auditService: AuditService,
    private findingService: FindingService,
    private capaService: CapaService,
    private evidenceService: EvidenceService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar) { }

  ngOnInit(): void {
    this.route.paramMap.pipe(
      switchMap((params: ParamMap): Observable<Capa> => {
        this.auditId = +params.get('auditId');
        this.findingId = +params.get('findingId');
        this.capaId = +params.get('capaId');
        this.sidId = +params.get('sidId');

        if (this.auditId > 0) {
          const b = {
            title: 'Audit:',
            path: `/audit/${this.auditId}`,
          };
          this.breadcrumbs.push(
            {
              title: 'All audits',
              path: '/audit',
            });
          this.breadcrumbs.push(b);
          this.auditService.getAudit(this.auditId)
            .subscribe(a => {
              this.audit = a;
              b.title = `Audit: ${a.auditTitle}`;
            });
        } else {
          this.breadcrumbs.push(
            {
              title: 'All Findings',
              path: '/audit/all/findings',
            });
        }
        if (this.findingId > 0) {
          const f = {
            title: 'Finding:',
            path: `/audit/${this.auditId}/finding/${this.findingId}`,
          };
          this.breadcrumbs.push(f);
          this.findingService.getFinding(this.findingId, this.auditId)
            .subscribe(x => {
              this.finding = x;
              f.title = `Finding: ${x.findingTitle}`;
            });
        }

        return this.capaService.getCapa(this.capaId);
      })
    )
      .subscribe(v => {
        this.capa = v;
        this.canApprove = !v.canApprove;        

        this.capaType = this.capaTypes.find(x => x.key === v.capaEcType).text;
        this.breadcrumbs.push({
          title: `CAPA: ${v.capaTitle}`,
          path: '',
        });
      });
    this.refreshData();    
  }

  ngAfterContentChecked(): void {
    this.evidenceTableOptions.columns[0].customTemplate = this.statusCellTemplate;
    this.evidenceTableOptions.columns[1].customTemplate = this.reasonCellTemplate;
    this.evidenceTableOptions.columns[3].customTemplate = this.fileCellTemplate;
  }

  refreshData(): void {    

    this.reloadEvidenceList()
      .subscribe(table => {
        this.evidenceTableData = table;
        this.hasHistory = table.hasHistory; 
        this.hasInitialEvidences = table.items.length > 0;
      });
  }

  showHistory(): void {
    this.router.navigateByUrl(`/evidence/history/${this.capa.id}`); 
  }

  refreshCapa(): void {
    this.capaService.getCapa(this.capaId)
      .subscribe(v => {
        this.capa = v;
        this.capaType = this.capaTypes.find(x => x.key === v.capaEcType).text;
      });
  }
  addEvidence(): void {
    const dialogRef = this.dialog.open(EvidenceAddDialogComponent, { data: { evidence: undefined, capaId: this.capaId } });

    dialogRef.afterClosed()
      .pipe(
        filter(res => res && res.success)
      )
      .subscribe(() => {
        if (!this.evidenceTableData || !this.evidenceTableData.totalCount) {
          this.reloadEvidenceList()
            .subscribe(table => {
              this.evidenceTableData = table;
              this.hasInitialEvidences = table.items.length > 0;
            });
          this.refreshCapa();
        }
        this.forceReloadEvent.emit(true);
      });
  }
  editEvidence(_id: number, data: Evidence): void {
    let copy;
    if (data) {
      copy = { ...data };
    }
    const dialogRef = this.dialog.open(EvidenceAddDialogComponent, { data: { evidence: copy, capaId: this.capaId } });

    dialogRef.afterClosed()
      .pipe(
        filter(res => res && res.success)
      )
      .subscribe(() => {
        this.refreshCapa();
        this.forceReloadEvent.emit(true);
      });
  }
  reloadEvidenceList(): Observable<TableData<Evidence>> {
    return this.evidenceService.getEvidenceList(this.capaId, 0, 0)
      .pipe(
        tap(val => this.calcCanSubmit(val))
      );
  }
  deleteData(id: number): Observable<RequestResult> {
    return this.evidenceService.deleteEvidence(id)
      .pipe(
        tap(() => this.refreshCapa())
      );
  }
  submitEvidence(): void {
    if (!(this.capa.canSubmitEvidence)) {
      return;
    }
    const dlgData = {
      data: {
        title: 'Are you sure you want to submit all Evidences?',
        text: '',
        okText: 'Submit',
      },
    };
    if (this.audit.draftFindingCount === 0) {
      dlgData.data.text =
        `<b>If you click the Submit button, no one will be able to add more evidences.</b><br> \
         <b>${this.evidenceTableData.totalCount} Evidences</b> are about to be submitted. \
        <br>You can't undo this action`;
    }
    const dialogRef = this.dialog.open(OkCancelDialogComponent, dlgData);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.capaService.submitEvidences(this.audit.id)
          .subscribe(() => {
            this.router.navigateByUrl(`/audit/${this.auditId}/finding/${this.findingId}/capa/${this.capaId}/approval}`);
          });
      }
    });
  }
  reasonPlaceholder(row: Evidence): string {
    if (row.status === EvidenceStatus.RETURN) {
      return 'Add reason (required)';
    } else if (row.status === EvidenceStatus.APPROVED) {
      return 'Add reason (optional)';
    }

    return 'Add reason';
  }
  calcCanSubmit(v: TableData<Evidence>): void {
    this.modelDirty = true;
    if (!v) {
      v = this.evidenceTableData;
    }
    this.canSubmit = v && v.items && v.items.findIndex(
      (val: Evidence, _idx: number) => !(val.status) || (val.status === EvidenceStatus.RETURN && !val.reason)) === -1;
  }
  finishLater(): void {
    if (this.modelDirty) {
      this.saveData()
        .subscribe((res: RequestResult) => {
          if (res.success) {
            if (this.sidId) {
              this.router.navigateByUrl(`/sid/${this.sidId}`);
            } else {
              this.router.navigateByUrl(`/audit/${this.auditId}/finding/${this.findingId}`);
            }
          }
        });
    } else {
      this.router.navigateByUrl(`/audit/${this.auditId}/finding/${this.findingId}`);
    }
  }
  saveData(): Observable<RequestResult> {
    const data: Array<EvidenceApproval> = this.evidenceTableData.items.map(
      x => ({ evidenceId: x.id, status: x.status, reason: x.reason }));

    return this.evidenceService.saveApprovals(this.capaId, data);
  }
  submitApproval(): void {

    if (!(this.canSubmit)) {
      return;
    }
    const dlgData = {
      data: {
        title: 'Are you sure you want to submit all Evidences?',
        text: '',
        okText: 'Submit',
      },
    };
    dlgData.data.text =
      `<b>${this.evidenceTableData.totalCount} Evidences</b> are about to be approved/return. \
      <br>You can't undo this action`;
    const dialogRef = this.dialog.open(OkCancelDialogComponent, dlgData);
    dialogRef.afterClosed()
      .subscribe(result => {
        if (result) {
          const data: Array<EvidenceApproval> = this.evidenceTableData.items.map(
            x => ({ evidenceId: x.id, status: x.status, reason: x.reason }));
          this.evidenceService.submitApprovals(this.capaId, data)
            .subscribe((res: EvidenceApprovalResult) => {
              this.snackBar.open(`CAPA Status updated to: ${res.resultStatus}`, 'Ok', {
                duration: 4000,
              });
              if (this.sidId) {
                this.router.navigateByUrl(`/sid/${this.sidId}`);
              } else {
                this.router.navigateByUrl(`/audit/${this.auditId}/finding/${this.findingId}`);
              }
            });
        }
      });

  }
}
